import { useErrorReporter } from '@/composables/useErrorReporter'
import {
	ACCOUNT_URL,
	API_BASE, API_ENDPOINT_AUTH_LOGIN,
	PORTAL_URL,
	SHARED_COOKIE_DOMAIN
} from '@/constants/index.js'
import type { LoginResponse } from '@/types/components/account'
import { useI18n } from 'vue-i18n'

const IGNORE_ERROR_CODES = [ 'AUT-017', 'AUT-018' ] // ignore 2FA required error
const TWO_FACTOR_MODAL_ID = 'two-factor-code-verification-modal'
const loading = ref(false)

type LoginRequestBody = {
	email: string
	password: string
	twoFactorCode?: number
}

class LoginError extends Error {
	status: number
	detail: string
	constructor(message: string, status: number, detail: string) {
		super(message)
		this.name = 'LoginError'
		this.status = status
		this.detail = detail
	}
}

export function useLogin(modalOpenRef: Ref<boolean>) {
	const { t } = useI18n({
		useScope: 'global'
	})
	const { bugsnagReport } = useErrorReporter()

	const login = async (email: string, password: string, twoFactorCode?: number) => {
		const formData: LoginRequestBody = {
			email,
			password
		}

		if (twoFactorCode) {
			formData.twoFactorCode = twoFactorCode
		}

		loading.value = true

		return await $fetch<LoginResponse>(`${API_BASE}${API_ENDPOINT_AUTH_LOGIN}`, {
			method: 'POST',
			credentials: 'include',
			body: formData,
			onResponse({ response }) {
				if (!response.ok) {
					const error = response._data?.errors?.[0] || response._data
					throw new LoginError(error.title || 'Request - Error submitting login credentials', error.status || 500, error.detail?.errorCode || '')
				}
				if (response._data?.success) {
					if (formData.twoFactorCode) {
						modalOpenRef.value = false
					}
					const redirectCookie = useCookie('redirected-from', {
						domain: SHARED_COOKIE_DOMAIN
					})
					const path = redirectCookie?.value || ''
					redirectCookie.value = ''
					let redirectPath = ''
					if (path.includes('kiosk')) {
						navigateTo(path)
						return
					} else if (response?._data?.redirect === 'portal') {
						redirectPath = `${PORTAL_URL}${path}`
					} else {
						redirectPath = `${ACCOUNT_URL}${path}`
					}
					window.location.href = redirectPath
				} else {
					bugsnagReport({
						error: new Error('Login failed'),
						showToast: true,
						toast: {
							title: t('login.error-toast'),
							message: t('login.error-toast-message')
						}
					})
				}
			}
		}).catch(e => {
			if (IGNORE_ERROR_CODES.includes(e.detail)) {
				// show 2FA modal but don't show the error toast
				modalOpenRef.value = true
			} else {
				bugsnagReport({
					error: new Error('Login failed'),
					showToast: true,
					toast: {
						title: e.status === 429 ? t('login.attempts-exceeded') : t('login.error-toast'),
						message: e.status === 429 ? t('login.attempts-exceeded-message') : t('login.error-toast-message')
					}
				})
			}
		}).finally(() => {
			loading.value = false
		})
	}

	return {
		loading,
		login,
		TWO_FACTOR_MODAL_ID
	}
}
